// ============================================================
// DocumentSearchView.tsx
// Sidebar Document Search zone — main-area takeover that filename-searches
// the firm's uploaded files (R2-backed FileAttachment rows) with optional
// client + case filters, via GET /api/documents/search (store action
// runDocumentSearch).
//
// ★ REDESIGN (approved mockup "casevo-document-search-redesign.html",
// Option A): standard 1180px column, the shared page head (eyebrow + 26px
// title + sub), a redesigned search bar + filter CHIPS, and results as
// Option-A CARD ROWS (file-type badge · filename with match highlight ·
// case link [locked treatment if no access] or client-level pill · uploader
// · date · size · Open / Download / Delete). A "Group by" segment (Date /
// Case) re-groups the SAME card rows under different headings.
//
// Search semantics UNCHANGED: a search runs when ANY of (filename ≥2 chars,
// case filter, client filter) is active, debounced 300ms; with nothing
// active, results clear back to null → the "Search for a document" state.
// ============================================================
import { useCanDeleteDocument } from '../utils/documentPermissions'
import React, { useEffect, useMemo, useState } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { useFirmChange } from '../utils/liveEvents'
import { initialPlaceParam, navigateTo, usePlaceParam } from '../navigation/navigate'
import { DocumentSearchResult } from '../types/documentSearch.types'
import { RawCase } from '../types/case.types'
import { fetchCases, requestDownloadUrlFlatApi, deleteFileFlatApi } from '../api/casevoApi'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload } from '../utils/documentAccess'
import { useCaseAccess, primeCaseAccess, type CaseAccessState } from '../hooks/useCaseAccess'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import SearchInput from '../components/shared/SearchInput'
import { SidebarHamburger } from '../components/layout/Sidebar'
import { formatFileSize } from '../components/shared/DocumentRow'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
import { LOCKED_CASE_TIP } from '../components/shared/CaseLink'
import { caseNameWithRef } from '../utils/caseName'
import { useLiveUserName } from '../hooks/useLiveUserName'
import UserAvatar from '../components/shared/UserAvatar'
import { SystemUploaderChip } from '../components/shared/EsignedTag'

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2
const GROUP_PREF_KEY = 'casevo_docsearch_groupby'

type GroupBy = 'date' | 'case'

// ── Mockup tokens (exact values) ─────────────────────────────
// Card shadow (mockup --shadow light / dark).
const SHADOW = 'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
const CARD = `bg-casevo-surface border border-casevo-border rounded-[14px] ${SHADOW}`
// Group heading (mockup .gh). Dark uses the main text token — readable
// (≥4.5:1 on the dark ground), not the dim soft tone.
const GH = 'flex items-center gap-[9px] text-[12px] font-bold tracking-[0.16em] uppercase text-[#5c6a61] dark:text-casevo-forest mx-[2px] mb-[10px]'

// ── Icons (Feather, currentColor) ────────────────────────────
type IconName = 'search' | 'chev' | 'x' | 'user' | 'brief' | 'open' | 'dl' | 'del' | 'lock' | 'file' | 'img' | 'grid' | 'docsearch' | 'nomatch'
const PATHS: Record<IconName, React.ReactNode> = {
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  chev: <path d="M6 9l6 6 6-6" />,
  x: <path d="M18 6L6 18M6 6l12 12" />,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 12 0v1" /></>,
  brief: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V4h8v3" /></>,
  open: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14L21 3" /></>,
  dl: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></>,
  del: <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
  img: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>,
  grid: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18" /></>,
  docsearch: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6" /><path d="M14 2v6h6" /><circle cx="16.5" cy="16.5" r="3.5" /><path d="M19 19l2.5 2.5" /></>,
  nomatch: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5M8 11h6" /></>,
}
const Ic: React.FC<{ n: IconName; className?: string; strokeWidth?: number }> = ({ n, className = 'w-4 h-4', strokeWidth = 2 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true">
    {PATHS[n]}
  </svg>
)

// ── File-type badge (mockup .ft 40×44, radius 9) ─────────────
type FileKind = 'pdf' | 'doc' | 'img' | 'xls' | 'gen'
const kindOf = (mime: string, filename: string): FileKind => {
  const m = (mime || '').toLowerCase()
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (m.startsWith('image/')) return 'img'
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv' || ['xls', 'xlsx', 'csv'].includes(ext)) return 'xls'
  if (m.includes('word') || m.includes('opendocument.text') || m === 'application/rtf' || ['doc', 'docx', 'rtf', 'odt'].includes(ext)) return 'doc'
  return 'gen'
}
const KIND_CLS: Record<FileKind, string> = {
  pdf: 'bg-[#fbeae7] text-[#c0392b] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]',
  doc: 'bg-[#e6eff9] text-[#2f6fb5] dark:bg-[rgba(143,184,234,0.14)] dark:text-[#8fb8ea]',
  img: 'bg-[#f1e9f8] text-[#8a5bb8] dark:bg-[rgba(201,166,234,0.14)] dark:text-[#c9a6ea]',
  xls: 'bg-[#e3f2ea] text-[#2d7a4a] dark:bg-[rgba(127,209,168,0.14)] dark:text-[#7fd1a8]',
  gen: 'bg-[#eef1ef] text-[#6b7280] dark:bg-[rgba(138,168,153,0.14)] dark:text-[#8aa899]',
}
const kindLabel = (kind: FileKind, filename: string): string => {
  const ext = filename.includes('.') ? filename.split('.').pop()!.toUpperCase() : ''
  if (ext && ext.length <= 4) return ext === 'DOCX' ? 'DOC' : ext === 'XLSX' ? 'XLS' : ext === 'JPEG' ? 'JPG' : ext
  return { pdf: 'PDF', doc: 'DOC', img: 'IMG', xls: 'XLS', gen: 'FILE' }[kind]
}
const FileBadge: React.FC<{ r: DocumentSearchResult }> = ({ r }) => {
  const kind = kindOf(r.mimeType, r.filename)
  return (
    <div className={`w-[40px] h-[44px] rounded-[9px] flex flex-col items-center justify-center gap-[2px] shrink-0 text-[9.5px] font-bold tracking-[0.06em] ${KIND_CLS[kind]}`} aria-hidden="true">
      <Ic n={kind === 'img' ? 'img' : kind === 'xls' ? 'grid' : 'file'} className="w-[17px] h-[17px]" />
      {kindLabel(kind, r.filename)}
    </div>
  )
}

// Highlight the (first) matched query substring in the filename — the
// mockup's faint gold <em> (rgba(224,182,74,.28) / .22 dark).
const Highlighted: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  const needle = q.trim()
  if (needle.length < SEARCH_MIN_CHARS) return <>{text}</>
  const i = text.toLowerCase().indexOf(needle.toLowerCase())
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-[rgba(224,182,74,0.28)] dark:bg-[rgba(224,182,74,0.22)] text-inherit rounded-[3px] px-[1px]">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  )
}

// ── Dates ────────────────────────────────────────────────────
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
const startOfWeek = (d: Date): number => {
  const day = (d.getDay() + 6) % 7 // Mon = 0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day).getTime()
}
// Right-hand date line: "Today, 09:42" / "Yesterday" / "Mon 21 Sep" (this
// week) / "12 Sep 2026".
const whenLabel = (iso: string, now: Date): string => {
  const d = new Date(iso)
  const t = d.getTime()
  if (t >= startOfDay(now)) return `Today, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
  if (t >= startOfYesterday(now)) return 'Yesterday'
  if (t >= startOfWeek(now)) return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
// Local midnight at the start of YESTERDAY — via the calendar (d-1), not
// "minus 24h", so a DST change doesn't shift the boundary by an hour.
const startOfYesterday = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).getTime()
const startOfMonth = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), 1).getTime()

// ★ Date-grouping bucket for an upload, all on the viewer's LOCAL calendar
// (same local-day rule as the calendar / My Work — never UTC):
//   Today → Yesterday → This week (earlier this week) → This month (earlier
//   this month) → one heading per MONTH ("August 2026") for this year and
//   last year → one heading per YEAR ("2024") beyond that, so a firm's
//   historical documents are split usefully instead of one "Earlier" heap.
// Each bucket carries a sortable key (higher = newer) so groups can be
// ordered newest-first regardless of input order.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const dateBucket = (iso: string, now: Date): { key: string; title: string; rank: number } => {
  const d = new Date(iso)
  const t = d.getTime()
  if (t >= startOfDay(now)) return { key: 'today', title: 'Today', rank: 4e6 }
  if (t >= startOfYesterday(now)) return { key: 'yesterday', title: 'Yesterday', rank: 3e6 }
  if (t >= startOfWeek(now)) return { key: 'week', title: 'This week', rank: 2e6 }
  if (t >= startOfMonth(now)) return { key: 'month', title: 'This month', rank: 1e6 }
  const y = d.getFullYear()
  const m = d.getMonth()
  if (y >= now.getFullYear() - 1) return { key: `m:${y}-${m}`, title: `${MONTHS[m]} ${y}`, rank: y * 12 + m }
  return { key: `y:${y}`, title: String(y), rank: y * 12 - 12 } // below every month of that year
}


// ── One Option-A card row ────────────────────────────────────
interface RowProps {
  r: DocumentSearchResult
  q: string
  now: Date
  access: CaseAccessState
  onOpenCase: (caseId: string) => void
  onOpenClient: (clientId: string) => void
  onOpen: (r: DocumentSearchResult) => void
  onDownload: (r: DocumentSearchResult) => void
  onDelete: (r: DocumentSearchResult) => void
  downloading: boolean
  deleting: boolean
}
const ACT = 'w-[34px] h-[34px] rounded-[8px] grid place-items-center border border-transparent transition-colors disabled:opacity-[0.55] disabled:cursor-not-allowed focus:outline-none casevo-focus'
const DocCardRow: React.FC<RowProps> = ({ r, q, now, access, onOpenCase, onOpenClient, onOpen, onDownload, onDelete, downloading, deleting }) => {
  // ★ Live uploader name by id (central firmUsers/authUser, patched on rename).
  const liveName = useLiveUserName()
  // (D12) An e-signed copy is uploaded by "Casevo e-signature" (system — no person).
  const uploaderName = r.uploadedBy ? (r.uploadedBy.system ? r.uploadedBy.name : liveName(r.uploadedBy.id, r.uploadedBy.name)) : ''
  const canDeleteDoc = useCanDeleteDocument() // (Batch 4) uploader / admin / deleteOthersDocuments
  const name = r.caseName ?? r.caseLabel ?? ''
  return (
    <div className={`${CARD} flex items-center gap-[14px] p-[13px_14px] mb-[10px] hover:border-casevo-mid hover:bg-[#f6faf8] dark:hover:bg-[#2b3b31] transition-colors`}>
      <FileBadge r={r} />
      <div className="flex-1 min-w-0">
        {/* Filename — the main text token in BOTH themes (readable in dark). */}
        <button type="button" onClick={() => onOpen(r)} title={`Open ${r.filename}`}
          className="block max-w-full text-left text-[14.5px] font-medium text-casevo-forest truncate hover:underline underline-offset-2 focus:outline-none casevo-focus rounded-[3px]">
          <Highlighted text={r.filename} q={q} />
        </button>
        <div className="flex items-center gap-[10px] mt-[5px] text-[12.5px] text-casevo-text-soft min-w-0">
          {r.caseId === null ? (
            r.clientId ? (
              // Client-level pill → opens that CLIENT (same store-driven path as
              // the case list / sidebar "Open client"). Own button, so it never
              // triggers the filename's open-document action.
              <button type="button"
                onClick={(e) => { e.stopPropagation(); onOpenClient(r.clientId!) }}
                title={r.clientName ? `Open ${r.clientName}` : 'Open client'}
                className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] whitespace-nowrap min-w-0 cursor-pointer hover:underline underline-offset-2 hover:brightness-95 dark:hover:brightness-125 focus:outline-none casevo-focus">
                <Ic n="user" className="w-[14px] h-[14px]" />
                <span className="truncate">Client-level{r.clientName ? ` · ${r.clientName}` : ''}</span>
              </button>
            ) : (
              <span className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] whitespace-nowrap min-w-0">
                <Ic n="user" className="w-[14px] h-[14px]" />
                <span className="truncate">Client-level</span>
              </span>
            )
          ) : access === 'locked' ? (
            <span role="link" aria-disabled="true" title={LOCKED_CASE_TIP}
              className="inline-flex items-center gap-[6px] text-[13px] font-medium text-casevo-text-soft opacity-75 cursor-not-allowed min-w-0">
              <Ic n="lock" className="w-[13px] h-[13px]" />
              <span className="truncate">{name}</span>
              {r.caseRef && <span className="font-mono text-[11px] tracking-[0.03em] font-normal">{r.caseRef}</span>}
              <span className="sr-only">({LOCKED_CASE_TIP})</span>
            </span>
          ) : (
            <button type="button" onClick={(e) => { e.stopPropagation(); onOpenCase(r.caseId!) }} disabled={access === 'unknown'}
              className="inline-flex items-center gap-[6px] text-[13px] font-medium text-casevo-brand dark:text-[#74c69d] hover:underline underline-offset-2 min-w-0 disabled:cursor-default disabled:no-underline focus:outline-none casevo-focus rounded-[3px]">
              <Ic n="brief" className="w-[14px] h-[14px]" />
              <span className="truncate">{name}</span>
              {r.caseRef && <span className="font-mono text-[11px] tracking-[0.03em] font-normal text-casevo-text-soft">{r.caseRef}</span>}
            </button>
          )}
          {r.uploadedBy && (
            <>
              <span className="opacity-50" aria-hidden="true">·</span>
              {r.uploadedBy.system ? (
                <span className="hidden sm:inline-flex items-center whitespace-nowrap"><SystemUploaderChip name={uploaderName} size="xs" /></span>
              ) : (
                <span className="hidden sm:inline-flex items-center gap-[6px] whitespace-nowrap">
                  <span aria-hidden="true" className="inline-flex"><UserAvatar userId={r.uploadedBy.id} fallbackName={r.uploadedBy.name} size="xs" title={false} /></span>
                  {uploaderName}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="hidden sm:flex w-[108px] shrink-0 flex-col items-end gap-[3px] text-[12.5px] text-casevo-text-soft">
        <b className="text-casevo-forest font-medium text-[13px] whitespace-nowrap">{whenLabel(r.uploadedAt, now)}</b>
        <span>{formatFileSize(r.sizeBytes)}</span>
      </div>
      <div className="flex items-center gap-[4px] shrink-0">
        <button type="button" onClick={() => onOpen(r)} disabled={downloading} title="Open" aria-label={`Open ${r.filename}`}
          className={`${ACT} text-casevo-mid hover:bg-casevo-input hover:text-casevo-forest hover:border-casevo-border`}>
          <Ic n="open" />
        </button>
        <button type="button" onClick={() => onDownload(r)} disabled={downloading} title="Download" aria-label={`Download ${r.filename}`}
          className={`${ACT} text-casevo-mid hover:bg-casevo-input hover:text-casevo-forest hover:border-casevo-border`}>
          {downloading
            ? <span className="w-[14px] h-[14px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-hidden="true" />
            : <Ic n="dl" />}
        </button>
        {/* (E-sign fixes B) An e-signed copy is read-only (server 409) — no Delete. */}
        {canDeleteDoc(r.uploadedBy?.id) && !r.esigned && !r.proofFor && (
        <button type="button" onClick={() => onDelete(r)} disabled={deleting} title="Delete" aria-label={`Delete ${r.filename}`}
          className={`${ACT} text-casevo-text-soft hover:bg-casevo-input hover:text-casevo-danger hover:border-casevo-border`}>
          <Ic n="del" />
        </button>
        )}
      </div>
    </div>
  )
}

// ── Filter chip wrapping a native <select> (full list, keyboard + a11y) ──
const FilterChip: React.FC<{
  label: string
  value: string
  valueLabel: string
  onChange: (v: string) => void
  disabled?: boolean
  children: React.ReactNode
}> = ({ label, value, valueLabel, onChange, disabled, children }) => {
  const set = value !== ''
  return (
    <div className={`relative inline-flex items-center gap-[7px] h-[44px] px-[13px] rounded-[10px] border text-[13.5px] whitespace-nowrap max-w-full focus-within:shadow-[0_0_0_3px_rgba(64,145,108,0.15)]
      ${set ? 'border-casevo-brand bg-casevo-mint text-casevo-forest dark:text-[#cfeedd]' : 'border-casevo-border bg-casevo-surface text-casevo-forest'} ${disabled ? 'opacity-60' : ''}`}>
      <span className={set ? 'text-casevo-brand' : 'text-casevo-text-soft'}>{label}</span>
      <span className="truncate max-w-[220px]">{valueLabel}</span>
      {set ? (
        <button type="button" onClick={() => onChange('')} aria-label={`Clear ${label.toLowerCase()} filter`}
          className="relative z-[1] text-casevo-brand hover:text-casevo-forest focus:outline-none casevo-focus rounded">
          <Ic n="x" className="w-[13px] h-[13px]" />
        </button>
      ) : <Ic n="chev" className="w-[13px] h-[13px] text-casevo-text-soft" />}
      {/* Transparent native select over the chip (minus the clear button). */}
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label={`Filter by ${label.toLowerCase()}`}
        className={`absolute inset-0 opacity-0 cursor-pointer ${set ? 'right-[30px]' : ''}`}>
        {children}
      </select>
    </div>
  )
}

// ── MAIN COMPONENT ───────────────────────────────────────────
const DocumentSearchView: React.FC = () => {
  const clients = useCasevoStore((s) => s.clients)
  const loadClients = useCasevoStore((s) => s.loadClients)
  const documentSearchResults = useCasevoStore((s) => s.documentSearchResults)
  const documentSearchError = useCasevoStore((s) => s.documentSearchError)
  const runDocumentSearch = useCasevoStore((s) => s.runDocumentSearch)
  const setDocumentSearchResults = useCasevoStore((s) => s.setDocumentSearchResults)

  // The search text + case / client filters are part of this screen's place
  // (?view=documents&q=…&docCase=…&docClient=…): a refresh or Back to the
  // search shows the same results (the search effect below re-runs).
  const [query, setQuery] = useState(() => initialPlaceParam('q') ?? '')
  const [selectedClientId, setSelectedClientId] = useState(() => initialPlaceParam('docClient') ?? '')
  const [selectedCaseId, setSelectedCaseId] = useState(() => initialPlaceParam('docCase') ?? '')
  usePlaceParam('q', query.trim() || null, (v) => setQuery(v ?? ''))
  usePlaceParam('docClient', selectedClientId || null, (v) => setSelectedClientId(v ?? ''))
  usePlaceParam('docCase', selectedCaseId || null, (v) => setSelectedCaseId(v ?? ''))
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentSearchResult | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // True while a (debounced) search request is in flight — drives the
  // "Searching…" line above existing results (skeletons when none yet).
  const [searching, setSearching] = useState(false)
  // Group-by (Date / Case) — persisted per browser.
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try { return window.localStorage.getItem(GROUP_PREF_KEY) === 'case' ? 'case' : 'date' } catch { return 'date' }
  })
  const chooseGroup = (g: GroupBy): void => {
    setGroupBy(g)
    try { window.localStorage.setItem(GROUP_PREF_KEY, g) } catch { /* private mode — non-fatal */ }
  }

  // Case filter source — locally fetched full case list.
  const [allCases, setAllCases] = useState<RawCase[]>([])
  const [casesLoading, setCasesLoading] = useState(false)
  useEffect(() => {
    let cancelled = false
    setCasesLoading(true)
    void fetchCases().then((data) => {
      if (!cancelled) { setAllCases(data); setCasesLoading(false) }
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[DocumentSearchView] Failed to load cases:', err)
      if (!cancelled) setCasesLoading(false)
    })
    return () => { cancelled = true }
  }, [])
  useEffect(() => { void loadClients() }, [loadClients])

  // (D12 live updates) Someone else added / removed a document (incl. an
  // e-signed copy) → the search on screen re-runs.
  const [liveRev, setLiveRev] = useState(0)
  useFirmChange(['documents', 'cases'], () => setLiveRev((n) => n + 1))

  // Debounced search trigger (unchanged semantics).
  useEffect(() => {
    const trimmed = query.trim()
    const hasQuery = trimmed.length >= SEARCH_MIN_CHARS
    const hasCaseFilter = selectedCaseId !== ''
    const hasClientFilter = selectedClientId !== ''
    if (!hasQuery && !hasCaseFilter && !hasClientFilter) {
      setDocumentSearchResults(null)
      setSearching(false)
      return
    }
    let stale = false
    const timer = setTimeout(() => {
      setSearching(true)
      void runDocumentSearch({
        q: hasQuery ? trimmed : undefined,
        caseId: hasCaseFilter ? selectedCaseId : undefined,
        clientId: hasClientFilter ? selectedClientId : undefined,
      }).finally(() => { if (!stale) setSearching(false) })
    }, SEARCH_DEBOUNCE_MS)
    return () => { stale = true; clearTimeout(timer) }
  }, [query, selectedCaseId, selectedClientId, runDocumentSearch, setDocumentSearchResults, liveRev])

  const rerun = (): void => {
    const trimmed = query.trim()
    setSearching(true)
    void runDocumentSearch({
      q: trimmed.length >= SEARCH_MIN_CHARS ? trimmed : undefined,
      caseId: selectedCaseId !== '' ? selectedCaseId : undefined,
      clientId: selectedClientId !== '' ? selectedClientId : undefined,
    }).finally(() => setSearching(false))
  }

  const handleHome = (): void => { void navigateTo({ view: 'caseList' }) }

  const handleOpenCase = async (caseId: string): Promise<void> => {
    try {
      // One history step (Back returns to this search). keepUntilShown:
      // switch to the case page as soon as the case row is mapped, not after
      // all its satellite loads.
      await navigateTo({ view: 'caseDetail', caseId }, { keepUntilShown: true, intents: (s) => s.setCaseOpenedFrom('documentSearch') })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DocumentSearchView] Failed to open case:', err)
    }
  }

  // Open a CLIENT's detail page — the shared store path (CaseListScreen /
  // CaseActionsMenu openClientDetails): push history, close takeovers (this
  // one included), then hand the id to ClientSearchView via pendingClientId.
  const handleOpenClient = async (clientId: string): Promise<void> => {
    await navigateTo({ view: 'clientDetail', clientId })
  }

  // OPEN — preview inline-safe types in a new tab, download the rest.
  const handleOpen = async (result: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(result.id)
    try {
      const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(result.id, true)
      if (isInlineSafeMime(mimeType)) openUrlInTab(downloadUrl)
      else await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to open file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  // DOWNLOAD — always an attachment.
  const handleDownload = async (result: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(result.id)
    try {
      const { downloadUrl, filename } = await requestDownloadUrlFlatApi(result.id)
      await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to download file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  // DELETE — confirm, then optimistic remove with restore on failure.
  const handleDeleteDocument = (result: DocumentSearchResult): void => {
    setActionError(null)
    setConfirmDeleteDoc(result)
  }
  const handleConfirmDelete = async (): Promise<void> => {
    const doc = confirmDeleteDoc
    if (!doc) return
    setConfirmDeleteDoc(null)
    setDeletingId(doc.id)
    const previousResults = useCasevoStore.getState().documentSearchResults
    setDocumentSearchResults(previousResults?.filter((d) => d.id !== doc.id) ?? null)
    try {
      await deleteFileFlatApi(doc.id)
    } catch (err) {
      setDocumentSearchResults(previousResults)
      setActionError(err instanceof Error ? err.message : 'Failed to delete document. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const trimmed = query.trim()
  const hasQuery = trimmed.length >= SEARCH_MIN_CHARS
  const hasAnySearchInput = hasQuery || selectedCaseId !== '' || selectedClientId !== ''
  const clientLabel = (id: string): string => {
    const c = clients.find((x) => x.id === id)
    return c ? `${c.title ? `${c.title} ` : ''}${c.forename} ${c.surname}`.trim() : 'Client'
  }
  const caseLabel = (id: string): string => {
    const c = allCases.find((x) => x.id === id)
    return c ? caseNameWithRef(c) : 'Case'
  }
  const clearAll = (): void => { setQuery(''); setSelectedClientId(''); setSelectedCaseId('') }

  // Case access for the locked treatment (results are server-filtered, so
  // this only bites if access changed mid-session — still honoured).
  const results = useMemo(() => documentSearchResults ?? [], [documentSearchResults])
  // Seed the access cache from the server's per-document flag BEFORE the hook
  // reads it, so the case link is correct on first paint (no neutral beat)
  // and no extra access-check request is needed. Rows without the flag (an
  // older backend) fall back to the hook's batched check.
  useMemo(() => { results.forEach((r) => primeCaseAccess(r.caseId, r.caseAccessible)) }, [results])
  const accessOf = useCaseAccess(results.map((r) => r.caseId))

  // Groups — SAME rows, different headings.
  const now = useMemo(() => new Date(), [documentSearchResults]) // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => {
    const out: { key: string; title: React.ReactNode; rows: DocumentSearchResult[] }[] = []
    const byKey = new Map<string, number>()
    const push = (key: string, title: React.ReactNode, r: DocumentSearchResult): void => {
      let i = byKey.get(key)
      if (i === undefined) { i = out.length; byKey.set(key, i); out.push({ key, title, rows: [] }) }
      out[i].rows.push(r)
    }
    if (groupBy === 'date') {
      // Newest-first within and across groups; only non-empty buckets exist.
      const sorted = [...results].sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
      const ranks = new Map<string, number>()
      for (const r of sorted) {
        const b = dateBucket(r.uploadedAt, now)
        ranks.set(b.key, b.rank)
        push(b.key, b.title, r)
      }
      out.sort((a, b) => (ranks.get(b.key) ?? 0) - (ranks.get(a.key) ?? 0))
    } else {
      // Results arrive newest-first → groups ordered by their newest document.
      for (const r of results) {
        if (r.caseId) {
          push(`case:${r.caseId}`, <>{r.caseName ?? r.caseLabel}{r.caseRef && <span className="font-mono normal-case tracking-[0.03em] font-medium text-casevo-text-soft">{r.caseRef}</span>}</>, r)
        } else {
          push(`client:${r.clientId ?? 'none'}`, <>Client-level{r.clientName ? <span className="normal-case tracking-[0.02em] font-medium text-casevo-text-soft">· {r.clientName}</span> : null}</>, r)
        }
      }
    }
    return out
  }, [results, groupBy, now])

  // Page-head sub line.
  const count = results.length
  const sub = !hasAnySearchInput
    ? 'Search every uploaded document across the firm'
    : documentSearchError ? 'Search couldn’t complete'
      : documentSearchResults === null ? 'Searching…'
        : count === 0 ? 'No matches'
          : null

  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader
        onWordmarkClick={handleHome}
        leftHamburger={<SidebarHamburger />}
        leftSlot={<BackButton />}
      />
      <div className="max-w-[1180px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[30px] pb-[60px]">
        {/* ── Page head (mockup .phead) ── */}
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div className="min-w-0">
            <p className="text-[12px] tracking-[0.18em] uppercase text-casevo-brand mb-[6px]">Document Search</p>
            <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">Find documents</h1>
            <p className="text-[13.5px] text-casevo-text-soft mt-[5px]" aria-live="polite">
              {sub ?? (
                <>
                  <b className="text-casevo-forest font-semibold">{count} {count === 1 ? 'document' : 'documents'}</b>
                  {hasQuery ? <> match “{trimmed}”</> : null}
                  {selectedClientId ? <> · {clientLabel(selectedClientId)}</> : null}
                  {selectedCaseId ? <> · {caseLabel(selectedCaseId)}</> : null}
                  {' · newest first'}
                </>
              )}
            </p>
          </div>
          <span className="flex-1" />
          {/* Group-by segment (Date / Case) — same card rows, different headings. */}
          <div className="inline-flex items-center gap-[8px] text-[13px] text-casevo-text-soft">
            <span id="docsearch-groupby">Group by</span>
            <div role="radiogroup" aria-labelledby="docsearch-groupby" className="inline-flex h-[34px] rounded-[8px] border border-casevo-border bg-casevo-surface overflow-hidden">
              {(['date', 'case'] as const).map((g, i) => (
                <button key={g} type="button" role="radio" aria-checked={groupBy === g} onClick={() => chooseGroup(g)}
                  className={`px-[12px] text-[13px] ${i > 0 ? 'border-l border-casevo-border' : ''} focus:outline-none casevo-focus transition-colors
                    ${groupBy === g ? 'bg-casevo-mint text-casevo-forest font-semibold dark:text-[#cfeedd]' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}>
                  {g === 'date' ? 'Date' : 'Case'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Search bar + filter chips (mockup .bar) ── */}
        <div className="flex flex-wrap items-center gap-2 mb-[22px]">
          <div className="relative flex-1 min-w-[260px]">
            <Ic n="search" className="w-4 h-4 absolute left-[14px] top-1/2 -translate-y-1/2 text-casevo-text-soft pointer-events-none z-[1]" />
            <SearchInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search by filename…"
              autoComplete="off"
              ariaLabel="Search documents by filename"
              className={`h-[44px] rounded-[10px] border border-casevo-border bg-casevo-surface text-casevo-forest text-[14.5px] pl-[37px] placeholder:text-casevo-text-soft ${SHADOW} focus:outline-none focus:border-casevo-mid focus:shadow-[0_0_0_3px_rgba(64,145,108,0.15)]`}
            />
            {query.length === 0 && (
              <span className="hidden sm:block absolute right-[14px] top-1/2 -translate-y-1/2 text-[11.5px] text-casevo-text-soft pointer-events-none">min 2 characters</span>
            )}
          </div>
          <FilterChip label="Client" value={selectedClientId} valueLabel={selectedClientId ? clientLabel(selectedClientId) : 'All clients'} onChange={setSelectedClientId}>
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.title ? `${c.title} ` : ''}{c.forename} {c.surname}</option>
            ))}
          </FilterChip>
          <FilterChip label="Case" value={selectedCaseId} valueLabel={selectedCaseId ? caseLabel(selectedCaseId) : (casesLoading ? 'Loading cases…' : 'All cases')} onChange={setSelectedCaseId} disabled={casesLoading}>
            <option value="">{casesLoading ? 'Loading cases…' : 'All cases'}</option>
            {allCases.map((c) => (
              <option key={c.id} value={c.id}>{caseNameWithRef(c)}</option>
            ))}
          </FilterChip>
        </div>

        {/* ── Results / states ── */}
        {!hasAnySearchInput ? (
          <div className={`${CARD} px-[30px] py-[54px] text-center`}>
            <div className="w-[64px] h-[64px] rounded-[18px] bg-casevo-mint text-casevo-brand grid place-items-center mx-auto mb-[18px]">
              <Ic n="docsearch" className="w-[28px] h-[28px]" strokeWidth={1.7} />
            </div>
            <h2 className="text-[20px] font-medium text-casevo-forest mb-2">Search for a document</h2>
            <p className="text-[14px] text-casevo-text-soft max-w-[440px] mx-auto leading-[1.55]">
              Type part of a filename, or pick a client or case to see everything uploaded to it.
            </p>
            <div className="flex justify-center gap-2 mt-5 flex-wrap">
              {['medical report', 'witness statement'].map((tip) => (
                <button key={tip} type="button" onClick={() => setQuery(tip)}
                  className="text-[12.5px] text-casevo-brand dark:text-[#7fd1a8] bg-casevo-mint rounded-full px-[11px] py-[5px] hover:underline underline-offset-2 focus:outline-none casevo-focus">
                  “{tip}”
                </button>
              ))}
            </div>
          </div>
        ) : documentSearchError ? (
          <div role="alert" className="flex flex-wrap items-center gap-[10px] px-[14px] py-[11px] rounded-[10px] text-[13.5px] mb-[14px] bg-[#fef2f2] border border-[#fecaca] text-casevo-danger dark:bg-[rgba(127,29,29,0.3)] dark:border-[rgba(185,28,28,0.4)]">
            <span>{documentSearchError}</span>
            <span className="flex-1" />
            <button type="button" onClick={rerun} className="text-[12px] tracking-[0.12em] uppercase underline hover:no-underline focus:outline-none casevo-focus rounded">Try again</button>
          </div>
        ) : documentSearchResults === null ? (
          <div aria-busy="true">
            <div className="flex items-center gap-2 text-[12.5px] text-casevo-text-soft -mt-[10px] mb-[14px] mx-[2px]">
              <span className="w-[14px] h-[14px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Searching filenames…
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-casevo-surface border border-casevo-border rounded-[14px] p-[14px] flex gap-[14px] items-center mb-[10px]">
                <div className="w-[40px] h-[44px] rounded-[9px] bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
                <div className="flex-1">
                  <div className="w-[48%] h-[13px] rounded-[6px] bg-[#e8f1ec] dark:bg-[#31443a] mb-[9px] animate-pulse motion-reduce:animate-none" />
                  <div className="w-[30%] h-[11px] rounded-[6px] bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
                </div>
                <div className="w-[90px] h-[12px] rounded-[6px] bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
              </div>
            ))}
          </div>
        ) : count === 0 ? (
          <div className={`${CARD} px-[30px] py-[54px] text-center`}>
            <div className="w-[64px] h-[64px] rounded-[18px] bg-[#e8f1ec] dark:bg-[#31443a] text-casevo-mid grid place-items-center mx-auto mb-[18px]">
              <Ic n="nomatch" className="w-[28px] h-[28px]" strokeWidth={1.7} />
            </div>
            <h2 className="text-[20px] font-medium text-casevo-forest mb-2">No documents found</h2>
            <p className="text-[14px] text-casevo-text-soft max-w-[440px] mx-auto leading-[1.55]">
              {hasQuery ? <>Nothing matches “{trimmed}”</> : <>No uploaded documents</>}
              {selectedClientId ? <> for {clientLabel(selectedClientId)}</> : null}
              {selectedCaseId ? <> on {caseLabel(selectedCaseId)}</> : null}
              . Try a shorter or different filename, or clear the filters.
            </p>
            <div className="mt-[18px] flex gap-2 justify-center">
              <button type="button" onClick={clearAll}
                className="inline-flex items-center gap-[6px] text-[12px] tracking-[0.12em] uppercase font-semibold text-casevo-brand border border-casevo-border rounded-[7px] px-[14px] py-[9px] bg-casevo-surface hover:border-casevo-mid focus:outline-none casevo-focus">
                <Ic n="x" className="w-[13px] h-[13px]" /> Clear filters
              </button>
            </div>
          </div>
        ) : (
          <>
            {searching && (
              <div className="flex items-center gap-2 text-[12.5px] text-casevo-text-soft -mt-[10px] mb-[14px] mx-[2px]">
                <span className="w-[14px] h-[14px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-hidden="true" />
                Updating results…
              </div>
            )}
            {actionError && (
              <div role="alert" className="flex items-center gap-[10px] px-[14px] py-[11px] rounded-[10px] text-[13.5px] mb-[14px] bg-[#fef2f2] border border-[#fecaca] text-casevo-danger dark:bg-[rgba(127,29,29,0.3)] dark:border-[rgba(185,28,28,0.4)]">
                {actionError}
              </div>
            )}
            {groups.map((g) => (
              <section key={g.key} className="mb-6" aria-label={typeof g.title === 'string' ? g.title : undefined}>
                <h2 className={GH}>
                  <span className="inline-flex items-center gap-[9px] min-w-0 truncate">{g.title}</span>
                  <span className="font-semibold tracking-[0.04em] text-casevo-text-soft">{g.rows.length}</span>
                  <span className="flex-1 h-px bg-casevo-border-soft" aria-hidden="true" />
                </h2>
                {g.rows.map((r) => (
                  <DocCardRow
                    key={r.id}
                    r={r}
                    q={query}
                    now={now}
                    access={r.caseId ? accessOf(r.caseId) : 'allowed'}
                    onOpenCase={(id) => { void handleOpenCase(id) }}
                    onOpenClient={(id) => { void handleOpenClient(id) }}
                    onOpen={(x) => { void handleOpen(x) }}
                    onDownload={(x) => { void handleDownload(x) }}
                    onDelete={handleDeleteDocument}
                    downloading={downloadingId === r.id}
                    deleting={deletingId === r.id}
                  />
                ))}
              </section>
            ))}
          </>
        )}
      </div>
      <ConfirmDialogue
        isOpen={confirmDeleteDoc !== null}
        title="Delete document?"
        message={
          confirmDeleteDoc
            ? `${confirmDeleteDoc.filename} will be permanently deleted. If it's attached to a case, it will also be removed from that case's documents. This cannot be undone.`
            : ''
        }
        confirmLabel={deletingId ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDelete() }}
        onCancel={() => setConfirmDeleteDoc(null)}
      />
    </div>
  )
}
export default DocumentSearchView
